import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoized login function to avoid recreating on every render
  const login = useCallback((tokenValue, userData) => {
    // Store token in cookie (httpOnly cookie is set by backend)
    // We store a flag in sessionStorage to know if user is authenticated
    sessionStorage.setItem('agentiq_authenticated', 'true');
    sessionStorage.setItem('agentiq_user', JSON.stringify(userData));
    setToken(tokenValue);
    setUser(userData);
  }, []);

  // Memoized logout function
  const logout = useCallback(() => {
    sessionStorage.removeItem('agentiq_authenticated');
    sessionStorage.removeItem('agentiq_user');
    setToken(null);
    setUser(null);
    
    // Call backend logout to clear httpOnly cookie
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(err => console.error('Logout error:', err));
  }, []);

  useEffect(() => {
    // Check for existing auth on mount
    const checkAuth = async () => {
      try {
        const isAuthenticated = sessionStorage.getItem('agentiq_authenticated');
        const storedUser = sessionStorage.getItem('agentiq_user');
        
        if (isAuthenticated && storedUser) {
          // Verify with backend (httpOnly cookie will be sent automatically)
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken('session'); // Flag to indicate authenticated
          } else {
            // Session expired or invalid
            sessionStorage.removeItem('agentiq_authenticated');
            sessionStorage.removeItem('agentiq_user');
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        sessionStorage.removeItem('agentiq_authenticated');
        sessionStorage.removeItem('agentiq_user');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Empty deps intentional - only run once on mount

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
